import { useEffect, useState } from "react";
import useAxios from "../../../../../hooks/useAxios";
import useFileDownloader from "../../../../../hooks/useFileDownloader";
import useToastWrapper from "../../../../../hooks/useToastWrapper";
import useLoading from "../../../../../shared/components/loading/useLoading";
import {
  catchFunction,
  dateRangeTransformer,
  getCurrentMonthRange,
} from "../../../../../shared/lib/utils";
import { useTracking } from "react-tracking";
import {
  DATA_EXPLORERS_ACTIONS_PERFORMANCE_DOWNLOAD,
  DATA_EXPLORERS_ACTIONS_PERFORMANCE_FILTER_SELECTABLES,
  DATA_EXPLORERS_ACTIONS_PERFORMANCE_PREVIEW,
} from "../../../../../shared/lib/apiEndpointsResolver";
import { DATA_EXPLORERS } from "../../../../../shared/lib/routesResolver";
import BreadcrumbPanel from "../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import {
  Collapsible,
  CollapsibleContent,
} from "../../../../../shared/components/collapsible/Collapsible";
import FixedScrollArea from "../../../../../shared/components/scroll-area/FixedScrollArea";
import ActionsPerformanceDataExplorerFilter from "./filter/ActionsPerformanceDataExplorerFilter";
import ActionsPerformanceDataExplorerPreview from "./preview/ActionsPerformanceDataExplorerPreview";
import ActionsPerformanceFilters from "../../shared/ActionsPerformanceFilters";

const ActionsPerformanceDataExplorerContainer = () => {
  const axios = useAxios();
  const { setLoading } = useLoading();
  const fileDownloader = useFileDownloader();
  const toast = useToastWrapper();
  const defaultDateValue = getCurrentMonthRange();
  const [filterSelectables, setFilterSelectables] = useState();
  const [isOpen, setIsOpen] = useState(true);
  const initialParamsValue = {
    fieldsFilter: {
      metrics: [],
      dimensions: [],
    },
    dataFilter: {
      dateRange: defaultDateValue,
      channels: [],
      platforms: [],
      strategies: [],
      clients: [],
      campaigns: [],
      markets: [],
      categories: [],
      products: [],
      customVars: [],
    },
  };
  const [params, setParams] = useState(initialParamsValue);
  const initialPreviewDataValue = { headers: [], rows: [] };
  const [previewData, setPreviewData] = useState(initialPreviewDataValue);
  const { trackEvent } = useTracking();

  const breadcrumbItems = [
    {
      title: "actionsPerformance.dataExplorer.breadcrumb.first",
      link: DATA_EXPLORERS,
    },
    { title: "actionsPerformance.dataExplorer.breadcrumb.second" },
  ];

  const updateDateFilterParams = (value) => {
    setParams({
      ...params,
      dataFilter: {
        ...params.dataFilter,
        dateRange: dateRangeTransformer(value),
      },
    });
  };

  const updateDataFilterParams = (key, value) => {
    setParams({
      ...params,
      dataFilter: { ...params.dataFilter, [key]: value },
    });
  };

  const updateFieldFilterParams = (key, value, isAddition) => {
    if (isAddition) {
      const values = [...params.fieldsFilter[key], value];
      setParams({
        ...params,
        fieldsFilter: { ...params.fieldsFilter, [key]: [...new Set(values)] },
      });
    } else {
      const values = [...params.fieldsFilter[key]];
      const newValues = values.filter((x) => x !== value);

      if (newValues.length < values.length) {
        setParams({
          ...params,
          fieldsFilter: { ...params.fieldsFilter, [key]: newValues },
        });
      }
    }
  };

  const isValidSubmission = () => {
    if (
      params.fieldsFilter.metrics.length > 0 &&
      params.fieldsFilter.dimensions.length > 0
    ) {
      return true;
    } else {
      toast.warning(`Debe seleccionar al menos una métrica y una dimensión.`);
      return false;
    }
  };

  const onSubmitHandler = () => {
    if (isValidSubmission()) {
      setLoading(true);
      axios
        .post(DATA_EXPLORERS_ACTIONS_PERFORMANCE_PREVIEW, params)
        .then((response) => {
          const rows = response.data.rows.map((row) => {
            return row.data.reduce(
              (accumulator, currentValue, currentIndex) => ({
                ...accumulator,
                [response.data.headers[currentIndex]]: currentValue,
              }),
              {},
            );
          });

          setPreviewData({ headers: response.data.headers, rows });
        })
        .catch(catchFunction)
        .finally(() => setLoading(false));
    }
  };

  const onDownloadHandler = (languageFormat) => {
    if (isValidSubmission()) {
      setLoading(true);
      const url = `${DATA_EXPLORERS_ACTIONS_PERFORMANCE_DOWNLOAD}?lng=${languageFormat}`;
      fileDownloader
        .download(url, params)
        .catch(catchFunction)
        .finally(() => setLoading(false));
    }
  };

  const fetchFilterSelectables = () => {
    axios
      .get(DATA_EXPLORERS_ACTIONS_PERFORMANCE_FILTER_SELECTABLES)
      .then((response) => setFilterSelectables(response.data))
      .catch(catchFunction);
  };

  useEffect(() => {
    fetchFilterSelectables();
    trackEvent({
      page: "actions-performance-data-explorer",
      event: "page-load",
    });
  }, []);

  return (
    <>
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <Collapsible
        className="w-full flex flex-row"
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <CollapsibleContent className="w-1/5">
          <FixedScrollArea className="h-[93vh]">
            <ActionsPerformanceFilters
              selectables={filterSelectables}
              defaultDateValue={defaultDateValue}
              onDateChangeValue={(value) => updateDateFilterParams(value)}
              onStrategyChangeValue={(value) =>
                updateDataFilterParams("strategies", value)
              }
              onChannelChangeValue={(value) =>
                updateDataFilterParams("channels", value)
              }
              onPlatformChangeValue={(value) =>
                updateDataFilterParams("platforms", value)
              }
              onClientChangeValue={(value) =>
                updateDataFilterParams("clients", value)
              }
              onCampaignChangeValue={(value) =>
                updateDataFilterParams("campaigns", value)
              }
              onMarketChangeValue={(value) =>
                updateDataFilterParams("markets", value)
              }
              onCategoryChangeValue={(value) =>
                updateDataFilterParams("categories", value)
              }
              onProductChangeValue={(value) =>
                updateDataFilterParams("products", value)
              }
              onCustomVarChangeValue={(value) =>
                updateDataFilterParams("customVars", value)
              }
            />
          </FixedScrollArea>
        </CollapsibleContent>
        <div
          className={`${isOpen ? "w-4/5" : "w-full"} flex flex-row bg-adq-gray`}
        >
          <div className="w-full bg-adq-gray">
            <div className="flex flex-col p-4 gap-y-4">
              <ActionsPerformanceDataExplorerFilter
                onMetricsChangeValue={(value, isAddition) =>
                  updateFieldFilterParams("metrics", value, isAddition)
                }
                onDimensionsChangeValue={(value, isAddition) =>
                  updateFieldFilterParams("dimensions", value, isAddition)
                }
              />
              <ActionsPerformanceDataExplorerPreview
                onSubmitHandler={onSubmitHandler}
                onDownloadHandler={onDownloadHandler}
                previewData={previewData}
              />
            </div>
          </div>
        </div>
      </Collapsible>
    </>
  );
};

export default ActionsPerformanceDataExplorerContainer;
